import { currentLocation } from '@/components/route-bits';
import type { PodKind } from '@/lib/api';

export type PhotoKind = PodKind | 'ATTEMPT';

/**
 * Proof photos are shrunk on the phone before sending (about 150–300 KB, Phase 3 plan 3):
 * longest side 1600 px, JPEG. If the browser cannot do it, the photo is sent as taken.
 */
export async function compressPhoto(file: File, maxSide = 1600, quality = 0.7): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

/**
 * Shrinks the photo and adds where the phone is and when it was taken, ready to send or to keep
 * on the phone until the signal is back. A photo without GPS is kept but does not count where
 * GPS is required.
 */
export async function prepareProofPhoto(file: File) {
  const [blob, where] = await Promise.all([compressPhoto(file), currentLocation(4000)]);
  return {
    blob,
    hasGps: !!where,
    fields: { takenAt: new Date().toISOString(), latitude: where?.latitude ?? null, longitude: where?.longitude ?? null },
  };
}
